import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';
import PrivacyBadge from '@/components/lite/PrivacyBadge';
import useLitePageView from '@/hooks/useLitePageView';

/**
 * Shared Lite layout — top bar, container, footer.
 * Pages use this as their wrapper.
 */
export default function LiteShell({ children, currentStep, totalSteps = 4 }) {
  const navigate = useNavigate();
  // Anonymous, daily-rotating-salt visit tracking (privacy-aligned)
  useLitePageView();

  return (
    <div className="min-h-screen bg-[#FBFAF7]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/us')}
            className="flex items-center gap-2 group"
            data-testid="lite-home-link"
          >
            <Heart className="w-5 h-5 text-primary" />
            <span className="font-serif text-lg text-primary-900">PlayFirst Lite</span>
            <span className="hidden sm:inline text-xs text-stone-500 ml-1">
              · Private AI workspace for SLPs
            </span>
          </button>
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={() => navigate('/')}
              className="text-stone-500 hover:text-primary transition-colors"
              data-testid="lite-international-link"
            >
              Full PlayFirst (intl)
            </button>
          </div>
        </div>
        {/* Progress bar */}
        {currentStep && (
          <div className="h-1 bg-stone-100">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              data-testid="lite-progress-bar"
            />
          </div>
        )}
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-100 mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3">
          <PrivacyBadge compact />
          <div className="text-xs text-stone-400">
            AI-generated drafts. Clinician judgment required.
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Step indicator pill row. Use inside any tool body.
 */
export function StepIndicator({ step, labels = ['Inputs', 'De-ID', 'Generate', 'Export'] }) {
  return (
    <div className="flex items-center gap-2 mb-6 text-xs" data-testid="lite-step-indicator">
      {labels.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <span
              className={[
                'inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium transition-colors',
                active ? 'bg-primary text-white' : '',
                done ? 'bg-emerald-100 text-emerald-700' : '',
                !active && !done ? 'bg-stone-100 text-stone-500' : '',
              ].join(' ')}
            >
              {idx}
            </span>
            <span className={active ? 'text-primary font-medium' : 'text-stone-500'}>
              {label}
            </span>
            {idx < labels.length && <span className="text-stone-300">·</span>}
          </div>
        );
      })}
    </div>
  );
}
